'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { dayjs, duration as dayjsDuration } from '@/libs';

dayjs.extend(dayjsDuration);

type UseTimerParams = {
  duration: number;
  interval?: number;
  manual?: boolean;
  expiresAt?: number | null;
  onFinish?: () => void;
};

type UseTimerReturn = {
  time: number;
  formatted: string;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
};

const formatTime = (ms: number): string => {
  const d = dayjs.duration(ms);
  const hours = d.hours();
  const minutes = String(d.minutes()).padStart(2, '0');
  const seconds = String(d.seconds()).padStart(2, '0');
  return hours > 0
    ? `${String(hours).padStart(2, '0')}:${minutes}:${seconds}`
    : `${minutes}:${seconds}`;
};

export function useTimer({
  duration,
  interval = 1000,
  manual = false,
  expiresAt,
  onFinish,
}: UseTimerParams): UseTimerReturn {
  const getRemaining = useCallback(
    () => (expiresAt != null ? Math.max(0, expiresAt - Date.now()) : duration),
    [expiresAt, duration],
  );

  const [time, setTime] = useState(getRemaining);
  const [isRunning, setIsRunning] = useState(() =>
    expiresAt != null ? expiresAt - Date.now() > 0 : !manual,
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const finishedRef = useRef(false);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    if (expiresAt != null) {
      const remaining = Math.max(0, expiresAt - Date.now());
      setTime(remaining);
      if (remaining <= 0) {
        clear();
        setIsRunning(false);
        if (!finishedRef.current) {
          finishedRef.current = true;
          onFinish?.();
        }
      }
      return;
    }

    setTime((prev) => {
      const next = prev - interval;
      if (next <= 0) {
        clear();
        setIsRunning(false);
        if (prev > 0) {
          onFinish?.();
        }
        return 0;
      }
      return next;
    });
  }, [expiresAt, interval, onFinish, clear]);

  const startInterval = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(tick, interval);
    setIsRunning(true);
  }, [interval, tick]);

  const start = useCallback(() => {
    clear();
    setTime(duration);
    setIsRunning(true);
    startInterval();
  }, [duration, clear, startInterval]);

  const pause = useCallback(() => {
    clear();
    setIsRunning(false);
  }, [clear]);

  const resume = useCallback(() => {
    clear();
    if (time > 0) {
      startInterval();
    }
  }, [clear, time, startInterval]);

  const reset = useCallback(() => {
    clear();
    setTime(duration);
    setIsRunning(!manual);
    if (!manual) {
      startInterval();
    }
  }, [duration, manual, clear, startInterval]);

  useEffect(() => {
    if (expiresAt != null) {
      finishedRef.current = false;
      const remaining = Math.max(0, expiresAt - Date.now());
      setTime(remaining);
      if (remaining > 0) {
        setIsRunning(true);
        startInterval();
      } else {
        clear();
        setIsRunning(false);
        finishedRef.current = true;
        onFinish?.();
      }
      return () => {
        clear();
      };
    }

    if (!manual) {
      startInterval();
    }
    return () => {
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual, expiresAt]);

  const formatted = useMemo(() => formatTime(time), [time]);

  return { time, formatted, isRunning, start, pause, resume, reset };
}
